import { parse as csvParse } from "csv-parse/browser/esm/sync";
import React from "react";
import ContactView from "./contact.jsx";
import date from "./dateAdapter.js";
import * as http from "./http.ts";
import WorkPartyMessageBoard from "./messageboard.jsx";
import * as utils from "./utils.jsx";

import { useBackendData, useFormState } from "./hooks.ts";

var FormField = utils.FormField;

function WorkPartyPeopleNeededPanel({ isACL, data }) {
  var vols = data.alert_low_registration;
  var acls = data.alert_acls_needed;
  var full = data.place_vols_on_waitlist;
  return (
    <div className="ui panel needed">
      {acls && isACL ? (
        <img
          src={window.base_url + "static/images/helmet-orange-needed.png"}
          title="ACLs Needed"
        />
      ) : (
        ""
      )}
      {vols && !full ? (
        <img
          src={window.base_url + "static/images/helmet-green-needed.png"}
          title="Extra Crew Members Needed"
        />
      ) : (
        ""
      )}
      {data.sawyers_needed && isACL ? (
        <img
          src={window.base_url + "static/images/sawyers-needed.png"}
          title="Sawyers Needed"
        />
      ) : (
        ""
      )}
    </div>
  );
}

function WorkPartyRegistrationCounts({ data, isACL }) {
  var leader_count = data.total_leaders_registered;
  var youth_count = data.number_of_youth_registrations;
  return (
    <div>
      <div className="crew-leader">
        Crew Leader: {data.leader ? data.leader.name : ""}
      </div>
      {isACL ? (
        <div className="crew-data">
          Leaders: {leader_count} | Total: {data.total_participants_to_display}
          {youth_count ? " (" + youth_count + " Youth)" : ""}
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

function WorkPartySpaceRemaining({ data }) {
  var count_on_waitlist =
    parseInt(data.total_invited_from_waitlist.toFixed()) +
    parseInt(data.total_on_waitlist.toFixed());
  if (count_on_waitlist) {
    return <h5>{count_on_waitlist} on waitlist</h5>;
  }
  var count = data.number_of_remaining_spots.toFixed();
  return <h5>{count} spaces left</h5>;
}

const CANCELLATION_REASONS = [
  "Sick or injured",
  "Scheduling conflict",
  "Weather conditions",
  "I feel unprepared",
  "Work seems too strenuous",
  "Transportation issue",
  "Other",
];

function CancelDialog({
  wp,
  user,
  action,
  closeModal,
  cancelReg = null,
  reg_status = "Cancelled",
}) {
  const cancelingSelf = !cancelReg;
  const { value, submitting, errors, handleChange, handleSubmit } =
    useFormState({
      initialValue: cancelReg ? { cancelReg, reg_status } : { otherRegs: [] },
      action,
      handleSubmitted,
    });
  const [submitted, setSubmitted] = React.useState(false);

  // If user is NOT canceling someone else from the Crew Corner roster,
  // also fetch linked registrations so they can choose which ones to include.
  const data = useBackendData({
    url: cancelReg ? null : wp.views.other_registrations,
  });

  function handleSubmitted() {
    if (cancelingSelf && value.cancellationReason) {
      setSubmitted(true);
    } else {
      closeModal();
    }
  }

  function handleCancel(e) {
    e.preventDefault();
    closeModal();
  }

  var other_reg = (data && data.other_registrations) || [];
  var options = [];
  for (var i = 0; i < other_reg.length; i++) {
    var reg = other_reg[i];
    options.push({
      value: reg.id,
      label: reg.first_name + " " + reg.last_name,
    });
  }
  return submitted ? (
    <div className="msg module">
      <div className="container">
        <header>Thank you for letting us know you can’t make it!</header>
        This allows others to join the work party. If you have any other
        thoughts you’d like to share on why you cancelled, please email{" "}
        <a href="mailto:volunteer@wta.org">volunteer@wta.org</a>.
      </div>
    </div>
  ) : (
    <div style={{ textAlign: "center" }}>
      <div style={{ margin: "0 auto", textAlign: "left" }}>
        {other_reg.length ? (
          <>
            <p>
              You registered the following people. Please select any
              registrations you'd like to cancel in addition to your own.
            </p>
            <div style={{ marginLeft: "1em" }}>
              <div>
                <span className="custom_checkbox">
                  <input
                    id="cancel-self-disabled"
                    type="checkbox"
                    disabled
                    checked
                    value=""
                  />
                  <label htmlFor="cancel-self-disabled">
                    {user.first_name} {user.last_name}
                  </label>
                </span>
              </div>
              <FormField
                type="checkboxes"
                name="otherRegs"
                options={options}
                inline={false}
                value={value.otherRegs}
                errors={errors}
                onChange={handleChange}
              />
            </div>
            <br />
          </>
        ) : null}
        <p>
          {cancelingSelf
            ? "Please select the reason that most closely matches why you are cancelling:"
            : reg_status === "No Show"
              ? "If known, please select the reason this crew member is a No Show:"
              : "If known, please select the reason for canceling this crew member:"}
          <br />
          <br />
          {CANCELLATION_REASONS.map((reason) => (
            <FormField
              key={reason}
              type="radio"
              name="cancellationReason"
              label={reason}
              checked={value.cancellationReason === reason}
              value={reason}
              errors={errors}
              onChange={handleChange}
            />
          ))}
        </p>
      </div>
      <p>
        Please click the Confirm button to confirm you'd like to{" "}
        {reg_status === "No Show" ? "record this No Show" : "cancel"}.
      </p>
      <button
        className="ui button center red"
        onClick={handleSubmit}
        disabled={submitting}
      >
        <span>{submitting ? "Confirming..." : "Confirm"}</span>
      </button>
      <button
        style={{ marginLeft: "1.5em" }}
        className="ui button center green"
        onClick={handleCancel}
        disabled={submitting}
      >
        No thanks
      </button>
    </div>
  );
}

function WorkParty({ data, user, isACL }) {
  const linkRef = React.useRef(null);

  function handleClick() {
    window.location.href = linkRef.current.getAttribute("href");
  }

  var isGreenhorn =
    user && user.incentive_days <= data.max_incentive_days_for_conditional_vols;
  var greenhorn_date = date.subDays(
    date.toDate(data.start_date_time),
    data.days_before_conditional_spots_expire,
  );
  var isOpenForGreenhorns = date.now().getTime() <= greenhorn_date.getTime();

  var show_spaces = data.show_space_remaining_in_vms;
  var status;
  var cutoff_days = data.registration_cutoff_in_days || 0;
  var cutoff = date.startOfDay(date.toDate(data.start_date_time));
  cutoff = date.subDays(cutoff, cutoff_days);
  var registration_closed = date.now().getTime() > cutoff.getTime();
  var place_on_waitlist;
  if (isACL) {
    place_on_waitlist =
      data.place_vols_on_waitlist && data.place_acls_on_waitlist;
  } else {
    place_on_waitlist = data.place_vols_on_waitlist;
  }
  if (
    data.conditional_spots_held_for_volunteers > 0 &&
    isGreenhorn &&
    data.remaining_spots_for_new_volunteers > 0 &&
    isOpenForGreenhorns &&
    !data.set_wp_to_full
  ) {
    place_on_waitlist = false;
  }

  if (data.work_party_status == "Published") {
    if (registration_closed) {
      status = "Registration Closed";
    } else if (place_on_waitlist) {
      if (
        data.max_waitlist_size &&
        data.total_on_waitlist > data.max_waitlist_size
      ) {
        status = "Full";
      } else {
        status = "Full - Join Waitlist";
      }
    } else {
      status = "See details";
    }
  } else {
    status = data.work_party_status;
  }

  var primary_trail_segment = utils.wpPrimarySegment(data);

  return (
    <div className="item js-item" onClick={handleClick}>
      <div className="ui panel status">{status}</div>
      {<WorkPartyPeopleNeededPanel data={data} isACL={isACL} />}
      <h1>
        <a ref={linkRef} href={window.base_url + "workparty/" + data._id + "/"}>
          {data.name}
        </a>
        <br />
        <span>{data.work_party_type.name}</span>
      </h1>
      <div className="row">
        <div className="col eight">
          <h2>
            <span className="ico-svg">
              <img
                src={window.base_url + "static/svg/calendar.svg"}
                width="20"
              />
            </span>{" "}
            <utils.DateRange
              start={data.start_date_time}
              end={data.end_date_time}
              end_only_if_different={true}
              showTime={false}
            />
          </h2>
        </div>
        {data.difficulty ? (
          <div className="col four">
            <h3>Trip Difficulty: {data.difficulty}</h3>
          </div>
        ) : (
          ""
        )}
      </div>
      <hr />
      <WorkPartyRegistrationCounts data={data} isACL={isACL} />
      <div style={{ clear: "left", height: "0" }}>&nbsp;</div>
      {data.work_party_groups
        ? data.work_party_groups.map((g) => (
            <h5 key={g.name}>Group: {g.name}</h5>
          ))
        : ""}
      {show_spaces && <WorkPartySpaceRemaining data={data} />}
      <hr />
      <div className="row">
        <div className="col four">
          <img
            src={utils.forceHttps(data.image_url_thumb)}
            title={data.photo_caption}
          />
        </div>
        <div className="col eight">
          {primary_trail_segment &&
          primary_trail_segment.hiking_guide_region ? (
            <div className="meta">
              <h3 style={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                <span className="ico-svg">
                  <img
                    src={window.base_url + "static/svg/location.svg"}
                    style={{ width: "12px" }}
                  />
                </span>{" "}
                Region
              </h3>
              <h3>
                {primary_trail_segment.hiking_guide_region}
                {primary_trail_segment.hiking_guide_sub_region
                  ? " — " + primary_trail_segment.hiking_guide_sub_region
                  : ""}
              </h3>
            </div>
          ) : (
            ""
          )}
          <div dangerouslySetInnerHTML={{ __html: data.overview }}></div>
          {data.features_highlights ? (
            <p>
              <strong>Features:</strong> {data.features_highlights.join(", ")}
            </p>
          ) : (
            ""
          )}
        </div>
      </div>
    </div>
  );
}

function WorkPartyStatus({ wp, user, my_registration }) {
  const status = my_registration.status;

  function openCrewCorner(e) {
    e.preventDefault();
    var trigger = document.querySelector('.ui.tabs li a[rel="#social"]');
    if (trigger) {
      trigger.click();
      window.scrollTo({
        top: trigger.getBoundingClientRect().top + window.scrollY,
        behavior: "smooth",
      });
    }
  }

  const reg = my_registration;
  var allow_guests = wp.allow_guests;
  var groups = utils.getGroupsForOrganizer(user, wp);
  var isAdmin = user && user.role === "Admin";
  var isCL =
    isAdmin ||
    (user && user.role === "Crew Leader") ||
    (user && user.lead_ambassador);
  var isACL = isCL || (user && user.role === "Assistant Crew Leader");
  var isGreenhorn =
    user && user.incentive_days <= wp.max_incentive_days_for_conditional_vols;
  var greenhorn_date = date.subDays(
    date.toDate(wp.start_date_time),
    wp.days_before_conditional_spots_expire,
  );
  var isOpenForGreenhorns = date.now().getTime() <= greenhorn_date.getTime();
  var place_on_waitlist = wp.place_on_waitlist;
  if (
    wp.conditional_spots_held_for_volunteers > 0 &&
    isGreenhorn &&
    wp.remaining_spots_for_new_volunteers > 0 &&
    isOpenForGreenhorns &&
    !wp.set_wp_to_full
  ) {
    place_on_waitlist = false;
  }
  var isCancelModalOpen = false;
  // If the page has the string `mbp-cancel` appearing in the URL query string, open the cancellation modal
  if (window.location.search.match(/mbp-cancel/)) {
    isCancelModalOpen = true;
  }

  var header = (
    <header>
      <h2>{user ? "Hi, " + user.first_name : "Join This Work Party"}</h2>
    </header>
  );
  if (status == "Invited from waitlist") {
    return (
      <div className="module msg">
        <div className="container">
          {header}
          <h3>
            You have been invited to register for this work party from the
            waitlist.
          </h3>
          <div className="module msg">
            <a href="confirm" className="ui button centered">
              Accept invitation
            </a>
          </div>
          <div className="module alert">
            <a href="decline" className="ui button centered">
              Decline invitation
            </a>
          </div>
        </div>
      </div>
    );
  } else if (
    (status === "Registered" ||
      status === "Applied" ||
      status === "Waitlisted" ||
      status === "Payment Pending") &&
    wp.work_party_status !== "Complete"
  ) {
    return (
      <div className="module msg">
        <div className="container">
          {header}
          {status === "Waitlisted" ? (
            <h3>You are on the waitlist for this work party.</h3>
          ) : (
            ""
          )}
          {status === "Applied" ? (
            <h3>You have applied for this work party.</h3>
          ) : (
            ""
          )}
          {status === "Registered" ? (
            <h3>You are registered for this work party.</h3>
          ) : (
            ""
          )}
          <utils.Modal
            open={isCancelModalOpen}
            action={wp.actions.cancel}
            Component={CancelDialog}
            wp={wp}
            user={user}
            onClosed={() => window.location.reload()}
          >
            <div className="module alert">
              <a href="cancel" className="ui button centered">
                Cancel Registration
              </a>
            </div>
          </utils.Modal>
        </div>
      </div>
    );
  } else if (wp.registration_closed) {
    return (
      <div className="module msg">
        <div className="container">
          {header}
          <h3>Registration for this work party has closed.</h3>
        </div>
      </div>
    );
  } else if (groups.length) {
    return (
      <div className="module msg">
        <div className="container">
          {header}
          {wp.work_party_status == "Preview" ? (
            <h3>This is a preview. Registration will open soon.</h3>
          ) : (
            groups.map((group) => (
              <div>
                <h3>
                  You are organizing the <strong>{group.name}</strong> group.
                </h3>
                <a
                  href=""
                  onClick={openCrewCorner}
                  className="green centered ui button"
                >
                  Manage my group
                </a>
              </div>
            ))
          )}
        </div>
      </div>
    );
  } else if (
    !isACL &&
    wp.work_party_groups &&
    !wp.group_wp_open_to_public &&
    !wp.requestedGroupId
  ) {
    return (
      <div className="module msg">
        <div className="container">
          {header}
          <h3>
            This is a private group work party. You must be invited to
            participate.
          </h3>
        </div>
      </div>
    );
  } else if (
    !isACL &&
    place_on_waitlist &&
    wp.max_waitlist_size &&
    wp.total_on_waitlist >= wp.max_waitlist_size
  ) {
    return (
      <div className="module neutral">
        <div className="container">
          {header}
          <h3>The waitlist for this work party is full.</h3>
        </div>
      </div>
    );
  } else {
    var register_action = wp.actions.register;
    var guest_warning =
      allow_guests || user ? (
        ""
      ) : (
        <h3>
          A My Backpack account is required. You will be asked to register or
          log in before you can join.
        </h3>
      );
    let join_message;
    if (register_action) {
      // Youth Vacations -> application form (see https://github.com/washingtontrails/vms/issues/551)
      if (wp.work_party_type.name == "Youth Volunteer Vacation") {
        join_message = (
          <div>
            <p>
              <a className="ui button centered" href={register_action}>
                Apply Now
              </a>
            </p>
            {place_on_waitlist ? (
              <p>
                Find other{" "}
                <a
                  href={
                    window.cms_base_url +
                    "volunteer/schedule?types=Youth%20Volunteer%20Vacation&includeWaitlisted=false"
                  }
                >
                  Youth Volunteer Vacations with no waitlist.
                </a>
              </p>
            ) : (
              ""
            )}
          </div>
        );
      } else {
        if (wp.requestedGroupId) {
          register_action += "?g=" + wp.requestedGroupId;
        }
        join_message = (
          <a
            href={register_action}
            className={
              "ui button centered" + (place_on_waitlist ? "" : " green")
            }
          >
            {place_on_waitlist
              ? "Join Waitlist"
              : wp.difficulty === "Strenuous"
                ? "Apply Now"
                : "Join Work Party"}
          </a>
        );
      }
    }
    if (wp.work_party_status == "Preview") {
      guest_warning = "";
      join_message = <h3>This is a preview. Registration will open soon.</h3>;
    }
    return (
      <div className={"module" + (place_on_waitlist ? " neutral" : " msg")}>
        <div className="container">
          {header}
          {guest_warning}
          {place_on_waitlist ? (
            <h3>
              This work party is currently full, but you may join the waitlist.
            </h3>
          ) : (
            ""
          )}
          {join_message}
        </div>
      </div>
    );
  }
}

function CarPoolForm({ user, action, initialValue, onSave, onCancel }) {
  const { value, errors, submitting, handleChange, handleSubmit } =
    useFormState({
      initialValue: {
        ...initialValue,
        reporterName: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
      },
      action,
      handleSubmitted,
    });

  function handleSubmitted() {
    onSave(value);
  }

  function handleCancel(e) {
    e.preventDefault();
    onCancel();
  }

  var notes_style = {
    position: "relative",
    padding: "11px",
    marginTop: "-11px",
  };
  var middle_style = { verticalAlign: "middle" };

  return (
    <form className="utility">
      <table>
        <tbody>
          <tr>
            <td style={middle_style}>
              {user.first_name} {user.last_name}
            </td>
            <td style={middle_style}>{user.email}</td>
            <td style={{ width: "50%" }}>
              <FormField
                type="select"
                name="carPool"
                value={value.carPool}
                errors={errors}
                onChange={handleChange}
              >
                <option>No Thanks</option>
                <option>Needs a ride</option>
                <option>Willing to drive</option>
                <option>Willing to drive or ride</option>
              </FormField>
            </td>
          </tr>
        </tbody>
      </table>
      <div style={notes_style}>
        {value.carPool && value.carPool != "No Thanks" ? (
          <div>
            <FormField
              type="text"
              name="carPoolNotes"
              errors={errors}
              placeholder="Message"
              value={value.carPoolNotes}
              onChange={handleChange}
            />
            Please list your preferred meeting point such as a park &amp; ride,
            neighborhood or town.
            <br />
          </div>
        ) : (
          ""
        )}
        <button
          className="ui button small"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            "Submitting..."
          ) : (
            <span>
              <span className="icon-ok"></span> Save
            </span>
          )}
        </button>
        <button
          style={{ marginLeft: "1em" }}
          className="ui button small red"
          onClick={handleCancel}
        >
          <span className="icon-cancel"></span> Cancel
        </button>
      </div>
    </form>
  );
}

function AddToGroupForm({ wp, action, groups, onAdd }) {
  const {
    value,
    setValue,
    error,
    errors,
    submitting,
    handleChange,
    handleSubmit,
  } = useFormState({
    action,
    handleSubmitted,
  });

  function handleSubmitted(data) {
    var group = groups.find((group) => group._id === value.groupId) || {};
    onAdd({
      status: data.status,
      first_name: value.firstName,
      last_name: value.lastName,
      email: value.email,
      under_18: value.under18,
      id: data.regId,
      role: "Volunteer",
      group: group.name,
      mine: true,
      awards: [],
      certifications: [],
      incentive_days: 0,
      notes: [],
    });
    // reset form
    setValue({ groupId: value.groupId });
  }

  function openDocuments(e) {
    e.preventDefault();
    var trigger = document.querySelector('.ui.tabs li a[rel="#documents"]');
    if (trigger) {
      trigger.click();
      window.scrollTo({
        top: trigger.getBoundingClientRect().top + window.scrollY,
        behavior: "smooth",
      });
    }
  }

  var register_url = wp.actions.register;
  var include_email = true;
  if (groups.length) {
    for (var i = 0; i < groups.length; i++) {
      if (value.groupId == groups[i]._id) {
        include_email = groups[i].organizer_must_provide_emails;
        break;
      }
    }
  }
  return (
    <form className="utility">
      <hr />
      <h2>Add a Crew Member</h2>
      <div className="row">
        <fieldset className="col two">
          <FormField
            type="text"
            name="firstName"
            placeholder="First name"
            required
            value={value.firstName}
            errors={errors}
            onChange={handleChange}
          />
        </fieldset>
        <fieldset className="col two">
          <FormField
            type="text"
            name="lastName"
            placeholder="Last name"
            required
            value={value.lastName}
            errors={errors}
            onChange={handleChange}
          />
        </fieldset>
        <fieldset className="col two">
          <FormField
            type="email"
            name="email"
            placeholder="Email"
            required={include_email || null}
            value={value.email}
            errors={errors}
            onChange={handleChange}
          />
        </fieldset>
        <fieldset className="col two">
          <FormField
            type="checkbox"
            name="under18"
            label="Under 18?"
            checked={value.under18}
            errors={errors}
            onChange={handleChange}
          />
        </fieldset>
        {groups.length ? (
          <fieldset className="col two">
            <FormField
              type="select"
              name="groupId"
              value={value.groupId}
              errors={errors}
              onChange={handleChange}
            >
              <option value="">No group</option>
              {groups.map((group) => (
                <option key={group._id} value={group._id}>
                  {group.name}
                </option>
              ))}
            </FormField>
          </fieldset>
        ) : (
          ""
        )}
        <div className="col two">
          <button
            className="green ui button"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Adding..." : "Add"}
          </button>
        </div>
        {error ? (
          <div className="validation-error">
            <div className="container">{error}</div>
          </div>
        ) : (
          ""
        )}
      </div>
      {groups.length && register_url ? (
        <div className="module msg">
          <header>
            <h2>Invite group members</h2>
          </header>
          {groups.map((group) => (
            <div className="container" key={group._id}>
              <p>
                If you would rather invite people to register themselves for the{" "}
                <strong>{group.name}</strong> group, send them this link:
                <br />
                <br />
                <a href={register_url + "?g=" + group._id}>
                  {register_url + "?g=" + group._id}
                </a>
              </p>
            </div>
          ))}
        </div>
      ) : (
        ""
      )}
      {groups.length ? (
        <div className="module msg">
          <header>
            <h2>Group member documents</h2>
          </header>
          <div className="container">
            <p>
              As the group organizer, you are responsible for making sure that
              your group members complete the necessary{" "}
              <a href="" onClick={openDocuments}>
                documents
              </a>
              .
            </p>
          </div>
        </div>
      ) : (
        ""
      )}
    </form>
  );
}

function SelectCrewMemberField({ name, onChange, users, selected }) {
  return (
    <div className="custom_select">
      <FormField type="select" name={name} required onChange={onChange}>
        <option key={name + "_empty"} value="">
          Select
        </option>
        {users.map((user) => (
          <option
            key={name + "_" + user.contact_id}
            value={user.contact_id}
            selected={user.contact_id === selected}
          >
            {user.first_name} {user.last_name} ({user.status}{" "}
            {user.role === "Volunteer" ? "Crew Member" : user.role})
          </option>
        ))}
      </FormField>
    </div>
  );
}

function IncidentResponseForm({ action, onSave, users }) {
  const [crewMemberInvolvement, setCrewMemberInvolvement] = React.useState([]);
  const [publicInvolvement, setPublicInvolvement] = React.useState([]);

  const {
    value,
    setValue,
    error,
    errors,
    submitting,
    handleChange,
    handleSubmit,
  } = useFormState({
    action,
    initialValue: {},
    handleSubmitted,
  });

  function addCrewmemberInvolvement() {
    setCrewMemberInvolvement([...crewMemberInvolvement, ""]);
  }

  function addPublicInvolvement() {
    setPublicInvolvement([...publicInvolvement, ""]);
  }

  function handleSubmitted() {
    onSave(value);
    setValue({});
  }

  return (
    <form className="utility">
      <div className="row">
        <div className="col three">
          <h3>Time</h3>
        </div>
        <div className="col seven">
          <fieldset className="col five">
            <p>
              <FormField
                type="date"
                name="incidentDate"
                placeholder="Date"
                errors={errors}
                value={value.incidentDate}
                required
                onChange={handleChange}
              />
            </p>
          </fieldset>
          <div className="col two">
            <p></p>
          </div>
          <fieldset className="col five">
            <p>
              <FormField
                type="time"
                name="incidentTime"
                placeholder="Time"
                errors={errors}
                value={value.incidentTime}
                required
                onChange={handleChange}
              />
            </p>
          </fieldset>
        </div>
      </div>
      <div className="row">
        <div className="col three">
          <h3>Details</h3>
        </div>
        <div className="col seven">
          <fieldset className="col five">
            <p>
              <FormField
                type="text"
                name="incidentLocation"
                placeholder="Location"
                errors={errors}
                required={true}
                onChange={handleChange}
                value={value.incidentLocation}
              />
            </p>
          </fieldset>
          <div className="col two">
            <p></p>
          </div>
          <fieldset className="col five">
            <p>
              <FormField
                type="select"
                name="incidentType"
                required
                onChange={handleChange}
                errors={errors}
                value={value.incidentType}
              >
                <option value="">- Type -</option>
                <option value="Injury">Injury</option>
                <option value="Near miss">Near miss</option>
                <option value="Illness">Illness</option>
                <option value="Behavior">Behavior</option>
                <option value="Exclusion">Exclusion</option>
                <option value="Equipment">Equipment</option>
              </FormField>
            </p>
          </fieldset>
        </div>
      </div>
      <div className="row">
        <div className="col three">
          <h3>Reporter</h3>
        </div>
        <div className="col seven">
          <p>
            <FormField
              type="text"
              name="reporterName"
              placeholder="Name of reporter"
              errors={errors}
              value={value.reporterName}
              required={true}
              onChange={handleChange}
            />
          </p>
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col twelve">
          <h2>People involved</h2>
        </div>
      </div>
      {crewMemberInvolvement.map((crew_member, index) => (
        <div className="row" key={index}>
          <div className="col three">
            <h3>Crew Member</h3>
          </div>
          <div className="col seven">
            <p>
              <SelectCrewMemberField
                name={"involvement_" + index}
                selected={crew_member}
                users={users}
                onChange={handleChange}
              />
            </p>
          </div>
        </div>
      ))}
      {publicInvolvement.map((mop, index) => (
        <div className="row">
          <div className="col three">
            <h3>Member of public</h3>
          </div>
          <div className="col seven">
            <p>
              <FormField
                type="text"
                name={"involvement_public_" + index}
                placeholder="Person's name"
                onChange={handleChange}
              />
            </p>
          </div>
        </div>
      ))}
      <div className="col three">&nbsp;</div>
      <div className="row">
        <div className="col three">&nbsp;</div>
        <div className="col three">
          <button
            type="button"
            className="green ui button"
            onClick={addCrewmemberInvolvement}
          >
            Select crew member
          </button>
        </div>
        <div className="col one">&nbsp;</div>
        <div className="col three">
          <button
            type="button"
            className="green ui button"
            onClick={addPublicInvolvement}
          >
            Add member of the public
          </button>
        </div>
      </div>
      <div className="row">
        <div className="col three">
          <h3>Witnesses</h3>
        </div>
        <div className="col seven">
          <p>
            <FormField
              type="text"
              name="witnesses"
              errors={errors}
              value={value.witnesses}
              required={true}
              onChange={handleChange}
            />
          </p>
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col twelve">
          <h2>Incident</h2>
          <p>
            BE SPECIFIC, detailed and factual. Do not editorialize or make
            assumptions.
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col three">
          <h3>Circumstances and actions leading up to incident</h3>
        </div>
        <div className="col seven">
          <p>
            <FormField
              type="textarea"
              name="circumstances"
              errors={errors}
              value={value.circumstances}
              style={{ height: "188px" }}
              onChange={handleChange}
            />
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col three">
          <h3>Describe incident and response</h3>
        </div>
        <div className="col seven">
          <p>
            <FormField
              type="textarea"
              name="descriptionResponse"
              errors={errors}
              value={value.descriptionResponse}
              style={{ height: "188px" }}
              onChange={handleChange}
            />
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col three">
          <h3>Actions taken after incident</h3>
        </div>
        <div className="col seven">
          <p>
            <FormField
              type="textarea"
              name="actionsafterIncident"
              errors={errors}
              value={value.actionsafterIncident}
              style={{ height: "188px" }}
              onChange={handleChange}
            />
          </p>
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col twelve">
          <h2>Witness statements</h2>
        </div>
      </div>
      <div className="row">
        <div className="col three">
          <h3>Witness one</h3>
        </div>
        <div className="col seven">
          <p>
            <FormField
              type="textarea"
              name="witness1Comments"
              errors={errors}
              value={value.witness1Comments}
              style={{ height: "188px" }}
              onChange={handleChange}
            />
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col three">
          <h3>Witness two</h3>
        </div>
        <div className="col seven">
          <p>
            <FormField
              type="textarea"
              name="witness2Comments"
              errors={errors}
              value={value.witness2Comments}
              style={{ height: "188px" }}
              onChange={handleChange}
            />
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col three">
          <h3>Witness three</h3>
        </div>
        <div className="col seven">
          <p>
            <FormField
              type="textarea"
              name="witness3Comments"
              errors={errors}
              value={value.witness3Comments}
              style={{ height: "188px" }}
              onChange={handleChange}
            />
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col three">
          <h3>Name or Anonymous Source Description</h3>
          <p>
            Area for opinion, feelings, assumptions and conjecture -- especially
            as it pertains to exclusionary incidents (from leader, volunteers,
            witnesses or anonymous source)
          </p>
        </div>
        <div className="col seven">
          <p>
            <FormField
              type="textarea"
              name="nameOrAnonymousSourceDescription"
              errors={errors}
              value={value.nameOrAnonymousSourceDescription}
              style={{ height: "188px" }}
              onChange={handleChange}
            />
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col two">
          <button
            className="green ui button"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit report"}
          </button>
        </div>
        {error ? (
          <div className="validation-error">
            <div className="container">{error}</div>
          </div>
        ) : (
          ""
        )}
      </div>
    </form>
  );
}

function UploadCrewMembersForm({ wp, groups }) {
  const [progress, setProgress] = React.useState(0);
  const [groupId, setGroupId] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [errorType, setErrorType] = React.useState(null);

  function handleChange(e) {
    setGroupId(e.target.value);
  }

  function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setErrorType(null);
    setError(null);

    var members_file = document.getElementById("members_file").files[0];
    var reader = new FileReader();
    var register_url = wp.actions.register_csv;
    reader.onload = (event) => {
      var text = event.target.result.trim();
      var expected =
        "firstName,lastName,email,under18,birthDate,phone,streetAddress,city,state,zip";
      if (text.indexOf(expected) != 0) {
        setSubmitting(false);
        setErrorType("Error");
        setError("Did not find expected columns: " + expected);
        return;
      }
      var new_members = csvParse(text, { columns: true });
      var counter = 0;

      var register_member = function () {
        if (groupId) {
          new_members[counter]["groupId"] = groupId;
        }
        new_members[counter]["under18"] =
          new_members[counter]["under18"] == "true";
        http
          .postJson(register_url, new_members[counter])
          .then(function () {
            counter++;
            setProgress((counter / new_members.length) * 100);
            if (counter < new_members.length) {
              register_member();
            }
            setSubmitting(false);
          })
          .catch((error) => {
            counter++;
            setErrorType("error on line " + (counter + 1) + ":");
            setError(error.state.error);
          });
      };

      register_member();
    };
    reader.readAsText(members_file);
  }

  return (
    <form className="utility">
      <hr />
      <h2>Upload a .csv Crew Member File</h2>
      <p>
        Allowed columns are: firstName (required), lastName (required), email,
        under18 (true/false), birthDate (YYYY-MM-DD), phone, streetAddress,
        city, state, zip.
      </p>
      <div className="row">
        <fieldset className="col four">
          <input type="file" id="members_file" name="members_file" />
        </fieldset>
        {groups.length && (
          <fieldset className="col two">
            <FormField
              type="select"
              name="groupId"
              value={groupId}
              onChange={handleChange}
            >
              <option value="">No group</option>
              {groups.map((group) => (
                <option key={group._id} value={group._id}>
                  {group.name}
                </option>
              ))}
            </FormField>
          </fieldset>
        )}
        <div className="col two">
          <button
            className="green ui button"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
      {submitting ? (
        <div className="row">
          <fieldset className="col six">
            <h3>
              <progress
                style={{ width: "100%" }}
                id="upload-progress"
                value={progress}
                max="100"
              />
            </h3>
          </fieldset>
        </div>
      ) : (
        ""
      )}
      {error ? (
        <div className="validation-error" style={{ margin: 5 }}>
          <h3>{errorType}</h3>
          <div>{error}</div>
        </div>
      ) : (
        ""
      )}
    </form>
  );
}

function WorkPartyCrewDocumentRow({ doc }) {
  return (
    <li key={doc._id}>
      <span className="icon-doc"></span> {doc.name}
      <a target="_blank" href={"document/" + doc._id}>
        review
      </a>
    </li>
  );
}

function WorkPartyAttachmentRow({ doc, icon }) {
  return (
    <li key={doc._id}>
      <span className={"icon-" + icon}></span> {doc.name}
      <a target="_blank" href={"attachment/" + doc._id}>
        review
      </a>
    </li>
  );
}

function WorkPartyCrewCorner({
  wp,
  user,
  my_registration,
  dependent_registrations,
}) {
  const [showCarpoolForm, setShowCarpoolForm] = React.useState(false);
  const [showIncidentResponseForm, setShowIncidentResponseForm] =
    React.useState(false);
  const [incidentReported, setIncidentReported] = React.useState(false);
  const [carpoolUpdate, setCarpoolUpdate] = React.useState(null);

  const [registrations, setRegistrations] = React.useState([]);
  const [volunteerCount, setVolunteerCount] = React.useState(0);
  const data =
    useBackendData({
      url: wp.views.registrations,
      afterLoad: (data) => {
        setRegistrations(data.registrations);
        setVolunteerCount(data.volunteer_count);
      },
    }) || {};

  function saveCarpool(value) {
    setCarpoolUpdate({
      ...value,
      carPoolNotes: value.carPool === "No Thanks" ? "" : value.carPoolNotes,
    });
    setShowCarpoolForm(false);
  }

  function reloadAfterCancel() {
    var newloc = document.location.origin + document.location.pathname;
    // there are a number of things that need to be updated when a
    // volunteer is cancelled, not all of which are under a react
    // component.  In order to save time finding and hanlding them all,
    // simply reload the document with the crew corner open, so that
    // context is not lost.
    newloc += "#social";
    document.location.assign(newloc);
    document.location.reload(true);
  }

  function addRegistration(reg) {
    var id = reg.id.substring(0, 15);
    var idx = registrations.findIndex((registration) => registration.id === id);
    setRegistrations(
      idx === -1
        ? [...registrations, reg]
        : registrations.map((registration, index) =>
            index === idx ? reg : registration,
          ),
    );
    setVolunteerCount(volunteerCount + (idx === -1 ? 1 : 0));
  }

  const reg = my_registration;
  var carpoolers = data.carpoolers || [];
  var my_carpool = data.my_carpool || {};
  var joined = registrations;
  var cp_form;
  var isCL = utils.hasRole(user, reg, "CL");
  var groups = utils.getGroupsForOrganizer(user, wp);
  var isAdmin = user.role === "Admin";
  var isACL = groups.length || isCL || utils.hasRole(user, reg, "ACL");
  var cl_docs = isCL ? wp.crew_corner_docs.CL : [];
  var acl_docs = isACL ? wp.crew_corner_docs.ACL : [];
  var medReq = wp.medical_form_required;
  var bcReq = wp.backcountry_form_required;
  var participant = wp.can_participate;
  var wpStatus = wp.work_party_status;
  var wpStarted = date.isBefore(date.toDate(wp.start_date_time), date.now());

  var role_map = {
    "Crew Leader": "CL",
    "Assistant Crew Leader": "ACL",
    Instructor: "Instructor",
    Volunteer: "Crew",
  };

  var can_view_roster =
    user.role === "Admin" ||
    user.role === "Crew Leader" ||
    user.role === "Assistant Crew Leader" ||
    user.role === "Land Manager";

  var middle_style = { verticalAlign: "middle" };

  if (
    !reg.id &&
    !dependent_registrations &&
    !groups.length &&
    !can_view_roster
  ) {
    return <div></div>;
  }

  if (carpoolUpdate) {
    my_carpool = {
      car_pool: carpoolUpdate.carPool,
      car_pool_notes: carpoolUpdate.carPoolNotes,
    };
    var i;
    for (i = 0; i < carpoolers.length; i++) {
      if (carpoolers[i]["mine"]) {
        carpoolers[i]["car_pool"] = carpoolUpdate.carPool;
        carpoolers[i]["car_pool_notes"] = carpoolUpdate.carPoolNotes;
      }
    }
  }

  var crew_rows = [];
  if (joined) {
    var last_role;
    crew_rows = joined.map(
      function (volunteer) {
        var style = {};
        if (last_role !== undefined && last_role != volunteer.role) {
          style.borderTop = "solid 2px #515E79";
        }
        last_role = volunteer.role;
        var showCancel =
          (isCL || (volunteer.mine && !wpStarted)) && wpStatus !== "Complete";
        if (
          volunteer.status !== "Registered" &&
          volunteer.status !== "Applied"
        ) {
          showCancel = false;
        }
        var awards = volunteer.awards.filter(function (award) {
          return utils.forceHttps(award.award.image_url);
        });
        var certifications = volunteer.certifications
          .filter((c) => c.active_status == "Active")
          .map((c) => c.certification.name);
        var award_images = awards.map((award) => (
          <img
            className={"award award-" + (award.status || "").toLowerCase()}
            src={award.award.image_url}
            title={award.award.name + ": " + award.status}
            style={{ maxWidth: 50, marginBottom: 0 }}
          />
        ));
        certifications.push(volunteer.incentive_days + " days experience");
        return (
          <tr key={volunteer.id} style={style}>
            <td>
              {isACL && volunteer.acl_applicant ? (
                <span
                  style={{
                    color: "orange",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  *{" "}
                </span>
              ) : (
                ""
              )}
              {isCL ? (
                <utils.Modal
                  contact_id={volunteer.contact_id}
                  Component={ContactView}
                  wp={wp}
                >
                  <a href="#">
                    {volunteer.first_name} {volunteer.last_name}
                  </a>{" "}
                  {volunteer.name_suffix || ""}
                </utils.Modal>
              ) : (
                <span>
                  {volunteer.first_name} {volunteer.last_name}{" "}
                  {volunteer.name_suffix || ""}
                </span>
              )}
              {isCL && volunteer.notes.length ? (
                <span
                  className="icon-doc-text"
                  title={"Notes (" + volunteer.notes.length + ")"}
                ></span>
              ) : (
                ""
              )}
              {awards && isCL ? award_images : ""}
              {volunteer.group ? <div>Group: {volunteer.group}</div> : ""}
              <div>{certifications.join(", ")}</div>
            </td>
            <td
              title={
                volunteer.role === "Volunteer" ? "Crew Member" : volunteer.role
              }
            >
              {role_map[volunteer.role]}
            </td>
            <td
              style={
                volunteer.status === "Waitlisted" ||
                volunteer.status === "Payment Pending" ||
                volunteer.status === "Applied"
                  ? { color: "#db9b3a" }
                  : {}
              }
            >
              {volunteer.status}
              {isCL && volunteer.problem && (
                <div className="Problems" title={volunteer.problem}>
                  Protective Action
                </div>
              )}
            </td>
            <td colSpan={showCancel || 2}>
              {volunteer.weekdays
                ? volunteer.weekdays.join(", ")
                : volunteer.days}
            </td>
            {showCancel ? (
              <td>
                <utils.Modal
                  Component={CancelDialog}
                  action={wp.actions.cancel}
                  wp={wp}
                  user={user}
                  cancelReg={volunteer.id}
                  reg_status={wpStarted ? "No Show" : "Cancelled"}
                  onClosed={reloadAfterCancel}
                >
                  <button
                    type="button"
                    style={{ marginTop: 0 }}
                    className="ui button small red"
                  >
                    <span>
                      <span className="icon-cancel"></span>{" "}
                      {wpStarted ? "No Show" : "Cancel"}
                    </span>
                  </button>
                </utils.Modal>
              </td>
            ) : null}
            {isCL ? (
              <td>
                <span
                  className={
                    volunteer.waiver_signed ? "icon-ok" : "icon-cancel"
                  }
                ></span>
              </td>
            ) : null}
            {isCL && medReq ? (
              <td>
                <span
                  className={volunteer.medical_form ? "icon-ok" : "icon-cancel"}
                ></span>
              </td>
            ) : null}
            {isCL && bcReq ? (
              <td>
                <span
                  className={
                    volunteer.backcountry_form ? "icon-ok" : "icon-cancel"
                  }
                ></span>
              </td>
            ) : null}
          </tr>
        );
      }.bind(null),
    );
  }
  var cp_rows = carpoolers.map((carpooler) => {
    return (
      <tr key={carpooler.id}>
        <td>
          {carpooler.first_name} {carpooler.last_name}
        </td>
        <td>
          <a href={"mailto:" + carpooler.email}>{carpooler.email}</a>
        </td>
        <td>
          {carpooler.car_pool} <em>"{carpooler.car_pool_notes}"</em>
        </td>
        <td style={{ padding: "0 11px" }}>
          {!carpooler.is_current_dependent || (
            <button
              className="ui button small"
              onClick={() => setShowCarpoolForm(true)}
            >
              Edit
            </button>
          )}
        </td>
      </tr>
    );
  });

  if (showCarpoolForm) {
    cp_form = (
      <CarPoolForm
        user={user}
        action={wp.actions.carpool_edit}
        onSave={saveCarpool}
        onCancel={() => setShowCarpoolForm(false)}
        initialValue={{
          carPool: my_carpool.car_pool,
          carPoolNotes: my_carpool.car_pool_notes,
        }}
      />
    );
  }

  return (
    <div>
      {isACL && (
        <div>
          <h2>Crew Leader Toolkit</h2>
          <ul className="options cart">
            {isCL ? (
              <li>
                <span className="icon-user"></span> Contact List
                <a target="_blank" href="print-contact-list.html">
                  review
                </a>
                <a target="_blank" href="print-contact-list.html#print">
                  print
                </a>
              </li>
            ) : (
              ""
            )}
            {isCL || isACL ? (
              <li>
                <span className="icon-doc"></span> Work Party Roster
                <a target="_blank" href="print-roster.html">
                  review
                </a>
                <a target="_blank" href="print-roster.html#print">
                  print
                </a>
              </li>
            ) : (
              ""
            )}
            {medReq &&
            (isCL ||
              (isACL &&
                wp.work_party_type.name == "Youth Volunteer Vacation")) ? (
              <li>
                <span className="icon-doc"></span> Medical Forms
                <a target="_blank" href="medical-forms.html">
                  review
                </a>
                <a target="_blank" href="medical-forms.html#print">
                  print
                </a>
              </li>
            ) : (
              ""
            )}
            {isCL && bcReq ? (
              <li>
                <span className="icon-doc"></span> Backcountry Experience Forms
                <a target="_blank" href="backcountry-forms.html">
                  review
                </a>
                <a target="_blank" href="backcountry-forms.html#print">
                  print
                </a>
              </li>
            ) : (
              ""
            )}
            {(isCL ||
              (isACL &&
                wp.work_party_type.name == "Youth Volunteer Vacation")) &&
            wp.work_party_type.name == "Youth Volunteer Vacation" ? (
              <li>
                <span className="icon-doc"></span> Youth Backcountry Experience
                Forms
                <a target="_blank" href="youth-backcountry-forms.html">
                  review
                </a>
                <a target="_blank" href="youth-backcountry-forms.html#print">
                  print
                </a>
              </li>
            ) : (
              ""
            )}
            {isCL ? (
              <li>
                <span className="icon-doc"></span> Completed Surveys
                <a target="_blank" href="feedback.html">
                  review
                </a>
                <a target="_blank" href="feedback.html#print">
                  print
                </a>
              </li>
            ) : (
              ""
            )}
            {cl_docs.map((doc) => (
              <WorkPartyCrewDocumentRow key={doc._id} doc={doc} />
            ))}
            {acl_docs.map((doc) => (
              <WorkPartyCrewDocumentRow key={doc._id} doc={doc} />
            ))}
            {wp.emergency_action_plans.map((doc) => (
              <WorkPartyAttachmentRow key={doc._id} doc={doc} icon="flag" />
            ))}
            {wp.trip_plans.map((doc) => (
              <WorkPartyAttachmentRow key={doc._id} doc={doc} icon="doc" />
            ))}
          </ul>
        </div>
      )}
      {wp.actions.report_incident && (
        <div>
          <h2>Report an incident</h2>

          {incidentReported ? (
            <div className="module alert">
              <p>
                <strong>Incident report saved</strong>
              </p>
            </div>
          ) : (
            ""
          )}

          {!incidentReported && showIncidentResponseForm ? (
            <div>
              <IncidentResponseForm
                onSave={() => {
                  setShowIncidentResponseForm(false);
                  setIncidentReported(true);
                }}
                users={registrations}
                action={wp.actions.report_incident}
              />
            </div>
          ) : (
            ""
          )}

          {!incidentReported && !showIncidentResponseForm ? (
            <button
              className="ui button small"
              onClick={() => setShowIncidentResponseForm(true)}
            >
              Begin report
            </button>
          ) : (
            ""
          )}

          <hr />
        </div>
      )}
      {groups.length
        ? groups.map((group) => (
            <div key={group._id} className="info module">
              You are an organizer for the <strong>{group.name}</strong> group.
              Manage your group members here.
            </div>
          ))
        : ""}
      {joined && (
        <div>
          <h2>Crew</h2>
          {isCL ? (
            <div style={{ textAlign: "right" }}>
              {data.problem_regs && (
                <p className="Problems">
                  At least one volunteer for this work party is under Protective
                  Action. Please call WTA at (206) 625-1367 for more
                  information.
                </p>
              )}
              <p>
                <strong>(R)</strong>elease,{" "}
                {medReq && (
                  <span>
                    <strong>(M)</strong>edical,
                  </span>
                )}{" "}
                {bcReq && (
                  <span>
                    <strong>(B)</strong>ackcountry Experience
                  </span>
                )}
              </p>
            </div>
          ) : (
            ""
          )}
          <div className="table-slider" style={{ overflowX: "scroll" }}>
            <table className="crew">
              <thead>
                <tr>
                  <th>Crew Member</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th colSpan={2}>Days</th>
                  {isCL ? <th title="Release">R</th> : null}
                  {isCL && medReq ? <th title="Medical">M</th> : null}
                  {isCL && bcReq ? (
                    <th title="Backcountry Experience">B</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>{crew_rows}</tbody>
            </table>
          </div>
          <br />
          {isACL && (
            <div>
              <p>{volunteerCount} Registered Green Hats</p>
              <p>
                <span
                  style={{
                    color: "orange",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  *
                </span>{" "}
                Current ACL Applicant
              </p>
            </div>
          )}
          {groups.length ? (
            <div>
              <AddToGroupForm
                wp={wp}
                groups={groups}
                action={wp.actions.register_group}
                onAdd={addRegistration}
              />
              <UploadCrewMembersForm wp={wp} groups={groups} />
            </div>
          ) : (
            ""
          )}
          {!groups.length && isCL ? (
            <div>
              <AddToGroupForm
                wp={wp}
                groups={[]}
                action={wp.actions.register_crew_member}
                onAdd={addRegistration}
              />
              <UploadCrewMembersForm wp={wp} groups={[]} />
            </div>
          ) : (
            ""
          )}
        </div>
      )}
      {wp.carpooling_enabled &&
      (reg.id || dependent_registrations || isAdmin) ? (
        <div>
          <hr />
          <br />
          <br />
          <h2>Carpool</h2>
          <p>
            The automated carpooling system provides a list of people who would
            like to share a ride. It is your responsibility to contact others
            and make all arrangements. For{" "}
            {dependent_registrations ? "other " : ""}
            youth participants the parent or guardian contact information is
            provided.
            {reg.id
              ? 'Click "Edit" now to add yourself to the list and view everyone who would like to carpool.'
              : ""}
          </p>
          <table>
            <thead>
              <tr>
                <th>Crew Member</th>
                <th>Email</th>
                <th colSpan={2}>Carpool</th>
              </tr>
            </thead>
            <tbody>
              {cp_rows}
              {reg.id ? (
                <tr>
                  <td style={middle_style}>
                    {user.first_name} {user.last_name} {user.name_suffix || ""}
                  </td>
                  <td style={middle_style}>{user.email}</td>
                  <td style={middle_style}>
                    {my_carpool.car_pool || "No Thanks"}{" "}
                    {my_carpool.car_pool_notes && (
                      <em>"{my_carpool.car_pool_notes}"</em>
                    )}
                  </td>
                  <td style={{ padding: "0 11px" }}>
                    {showCarpoolForm || (
                      <button
                        className="ui button small"
                        onClick={() => setShowCarpoolForm(true)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                ""
              )}
            </tbody>
          </table>
          {cp_form}
          <br />
          <br />
        </div>
      ) : (
        ""
      )}
      <hr />
      <br />
      <br />
      {participant && <WorkPartyMessageBoard wp={wp} />}
    </div>
  );
}

function WorkPartyDocuments({ data: prefetchedData, wp, user, views }) {
  const backendData = useBackendData({
    url: user && views ? views.documents : null,
  });
  const data = prefetchedData || backendData;

  function renderDocuments(documents) {
    return documents.map((doc) => {
      var complete = doc.completed;
      let document_button;
      let document_link;
      if (doc.contactId) {
        if (doc.formUrl) {
          var form_url = doc.formUrl;
          var query = [];
          if (doc.contactIdParam)
            query.push(doc.contactIdParam + "=" + doc.contactId);
          if (doc.contactNameParam)
            query.push(doc.contactNameParam + "=" + doc.name);
          if (doc.regIdParam && doc.regId)
            query.push(doc.regIdParam + "=" + doc.regId);
          if (doc.workPartyNameParam && wp)
            query.push(doc.workPartyNameParam + "=" + wp.name);
          if (query.length)
            form_url +=
              (form_url.indexOf("?") == -1 ? "?" : "&") + query.join("&");
          if (complete) {
            document_link = (
              <a className="view" href={form_url} target="_blank">
                Complete Online
              </a>
            );
          } else {
            document_button = (
              <a className="button" href={form_url} target="_blank">
                Complete Online
              </a>
            );
          }
        }
      }
      var completed_text = complete ? (
        <p>
          <strong>DONE!</strong>
        </p>
      ) : (
        ""
      );
      var printable_link = doc.printableUrl ? (
        <a className="view" href={doc.printableUrl} target="_blank">
          <p>Printable version</p>
        </a>
      ) : (
        ""
      );
      var icon_style = complete ? {} : { fontSize: "4em" };
      var icon_name = complete ? "icon-ok" : "icon-doc-text";
      return (
        <div
          className={"ui document" + (complete ? " complete" : "")}
          key={doc.contactId + doc.docId}
        >
          <h4>{doc.docName}</h4>
          <p>{doc.name}</p>
          <span className={icon_name} style={icon_style}></span>
          {document_button}
          {completed_text}
          {document_link}
          {printable_link}
        </div>
      );
    });
  }

  var documents;
  if (data) {
    documents = [];
    data.forEach((wrapper) => {
      if (wrapper.documentInfos) {
        wrapper.documentInfos.forEach((doc) => {
          documents.push(doc);
        });
      }
    });
  } else if (wp) {
    documents = wp.documents.map(function (d) {
      return {
        docId: d._id,
        docName: d.document_display_name,
        formUrl: d.survey_link,
        printableUrl: d.printable_form_link,
      };
    });
  } else {
    documents = [];
  }
  let email = "volunteer@wta.org";
  if (wp && wp.work_party_type && wp.work_party_type.name == "Outreach Event") {
    email = "outreach@wta.org";
  }
  return (
    <section>
      <p>
        Please see below for the status of required documents for this work
        party. These forms must be completed prior to registering for the event.
        If you are unable at access the forms, or are not sure of the status
        associated with each, please contact{" "}
        <a href="mailto:{email}">{email}</a>.
      </p>
      {!user && (
        <p>
          <a
            className="green ui button"
            href={window.cms_base_url + "/login"}
            style={{ textDecoration: "none" }}
          >
            Sign in to complete online
          </a>
        </p>
      )}
      <div>
        {wp && (
          <div className="ui document">
            <h4>Release Waiver</h4>
            <p>
              You will be required to sign a release waiver when you sign in at
              the trailhead.
            </p>
            <a className="view" href={wp.views.waiver} target="_blank">
              <p>Preview Waiver</p>
            </a>
          </div>
        )}
        {renderDocuments(documents)}
      </div>
    </section>
  );
}

function WorkPartyPreregDocuments({ data, wp = null, user, views = {} }) {
  function handleClick(event) {
    event.preventDefault();
    window.location.reload();
  }

  return (
    <div>
      <h1>Required Documentation</h1>
      <p>Before you can register, you must complete the following forms:</p>
      <section className="module highlight">
        <div className="container">
          <WorkPartyDocuments data={data} wp={wp} user={user} views={views} />
        </div>
        <section className="cta">
          <p>
            <a
              className="green ui button"
              href={"#"}
              style={{ textDecoration: "none" }}
              onClick={handleClick}
            >
              I have completed the documents
            </a>
          </p>
        </section>
      </section>
    </div>
  );
}

export {
  WorkParty,
  WorkPartyCrewCorner,
  WorkPartyDocuments,
  WorkPartyPreregDocuments,
  WorkPartyStatus,
};
